import { signInWithCustomToken } from "firebase/auth";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button, Card, CardContent, StatusBanner } from "@secure-vault/ui";
import { sendTrusteeLink, verifyTrusteeLink } from "@secure-vault/api";
import { auth } from "@/lib/firebase";

/**
 * Landing page for the trustee magic link (vault app).
 *
 * Two modes:
 *  - With `challenge_id` + `token` in the query: consume the link, sign the trustee
 *    in with the returned Firebase custom token (SECONDARY/vault project), then
 *    forward to `/trustee/unlock` PRESERVING the URL fragment (the Trustee_Secret
 *    rides in `#secret=…`, never the query string, and must be carried through
 *    untouched).
 *  - Without them: show a "request access link" form so a trustee who arrived without
 *    a fresh link can be emailed one.
 */
export function TrusteeClaimContainer() {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const challenge_id = params.get("challenge_id") ?? undefined;
  const token = params.get("token") ?? undefined;
  const client_id = params.get("client_id") ?? undefined;

  const [error, setError] = useState<string | null>(null);
  const hasVerified = useRef(false);

  // ── Request-link form state (no token present) ──
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const hasLink = !!challenge_id && !!token && !!client_id;

  useEffect(() => {
    if (!hasLink || hasVerified.current) return;
    hasVerified.current = true;

    verifyTrusteeLink(challenge_id!, token!, client_id!)
      .then(async ({ custom_token }) => {
        await signInWithCustomToken(auth, custom_token);
        // Forward to the unlock portal, carrying the fragment (Trustee_Secret) intact.
        const fragment = window.location.hash; // includes leading "#", may be empty
        window.location.replace(`/trustee/unlock${fragment}`);
      })
      .catch((err: unknown) => {
        const status =
          typeof err === "object" && err && "status" in err
            ? Number((err as { status?: unknown }).status)
            : undefined;
        if (status === 401 || status === 403) {
          setError(
            "This link has expired or has already been used. Request a new access link below."
          );
        } else if (status === 429) {
          setError("Too many attempts. Please wait a moment and try again.");
        } else {
          setError("Something went wrong. Request a new access link below.");
        }
      });
  }, [hasLink, challenge_id, token, client_id]);

  async function handleRequestLink() {
    if (!email.trim() || !client_id) return;
    setSending(true);
    setError(null);
    try {
      await sendTrusteeLink(email.trim(), client_id);
      // Enumeration-safe on the backend: always treat as success.
      setSent(true);
    } catch {
      setError("Unable to send an access link. Please try again later.");
    } finally {
      setSending(false);
    }
  }

  // Verifying an in-flight link (and no error yet).
  if (hasLink && !error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-primary size-8 animate-spin" />
          <p className="text-muted-foreground text-sm">Checking your link…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="bg-muted flex w-full flex-col items-center justify-center p-8">
        <Card className="w-full max-w-sm">
          <CardContent className="flex flex-col gap-6 p-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="bg-primary/10 flex size-12 items-center justify-center rounded-full">
                <ShieldCheck className="text-primary size-6" />
              </div>
              <h1 className="text-2xl font-semibold">Access a vault</h1>
              <p className="text-muted-foreground text-sm">
                {sent
                  ? "If you are a trustee for this vault, a link is on its way to your inbox."
                  : "Enter your email and we'll send you a link to continue."}
              </p>
            </div>

            {error && <StatusBanner error={error} />}

            {!sent && (
              <>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-background border-border focus:ring-primary w-full rounded border p-2.5 text-sm focus:ring-1 focus:outline-none"
                />
                <Button
                  className="w-full"
                  size="lg"
                  disabled={sending || !email.trim() || !client_id}
                  onClick={handleRequestLink}
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-1.5 size-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="mr-1.5 size-4" />
                      Send access link
                    </>
                  )}
                </Button>
                {!client_id && (
                  <p className="text-muted-foreground text-center text-xs">
                    Please use the link sent to you by Secure Legacy.
                  </p>
                )}
              </>
            )}

            {sent && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSent(false);
                  setError(null);
                }}
              >
                Use a different email
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
